import type {OFetchError} from '#imports';
import {useLoadable, useTranslation} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {FetchError, type IdentityRef} from '@api';
import {useApis} from '~/composables';

type TranslationFn = ReturnType<typeof useTranslation>['t'];

function handleAdvancedSearchApiError(e: OFetchError, t: TranslationFn, customMessage?: string) {
    const error = new FetchError(e, e.message);
    if (error.statusCode === 400) {
        if (error.hasValidationErrors()) {
            const advancedSearchErrors: Record<string, string> = {
                HARMFUL_CONTENT: t('search.advanced.home-v2.generation.error.harmful.content'),
                NOT_ENOUGH_INFORMATION: t('search.advanced.home-v2.generation.error.not.enough.information'),
                NOT_SUPPORTED_FORMAT: t('search.advanced.home-v2.generation.error.not.supported.format'),
            };
            const errors = error.getValidationErrors(t);

            if ('advancedSearch' in errors) {
                errors.advancedSearch.forEach((errorCode: string) => {
                    const message = advancedSearchErrors[errorCode] ?? customMessage ?? t('search.advanced.home-v2.pdf.generation.error.generic');
                    pushVJoySnackbar({
                        props: {
                            header: t('search.advanced.home-v2.generation.error.header'),
                            message,
                            level: 'error',
                        },
                    });
                });
            }
        }
    }

    if (error.statusCode === 413) {
        pushVJoySnackbar({
            props: {
                header: t('search.advanced.home-v2.generation.error.header'),
                message: t('search.advanced.home-v2.generation.error.413'),
                level: 'error',
            },
        });
    }

    if (error.statusCode === 500) {
        pushVJoySnackbar({
            props: {
                header: t('search.advanced.home-v2.generation.error.header'),
                message: t('search.advanced.home-v2.generation.error.technical'),
                level: 'error',
            },
        });
    }
}
export function useAdvancedSearchPromptApi(customMessage?: string | undefined) {
    const {advancedSearchApi} = useApis();
    const {t} = useTranslation();
    const {loading, execute} = useLoadable(
        async (prompt: string, onBehalfOf?: IdentityRef, referralId?: string) => {
            const {headers} = await advancedSearchApi.createAdvancedSearchFromPromptRaw({
                createAdvancedSearchFromPromptRequest: {
                    prompt,
                    onBehalfOf,
                    origin: 'USER_PROMPT',
                    submittedWithEnterKey: false,
                    referralId,
                },
            });
            return {
                redirectUrl: headers.get('Location')!,
            };
        },
        {
            alertAboutErrors: true,
            fetchErrorHandler: (e) => handleAdvancedSearchApiError(e, t, customMessage),
        },
    );

    return {loading, createAdvancedSearchFromPrompt: execute};
}

export function useAdvancedSearchFileUploadApi(customMessage?: string | undefined) {
    const {advancedSearchAuthenticatedApi} = useApis();
    const {t} = useTranslation();
    const {loading, execute} = useLoadable(
        async (file: Blob, onBehalfOfIdentity?: string, referralId?: string) => {
            const {headers} = await advancedSearchAuthenticatedApi.createAdvancedSearchFromFileUploadRaw({file, onBehalfOfIdentity, referralId});
            return {
                redirectUrl: headers.get('Location')!,
            };
        },
        {
            alertAboutErrors: true,
            fetchErrorHandler: (e) => handleAdvancedSearchApiError(e, t, customMessage),
        },
    );

    return {loading, createAdvancedSearchFromFile: execute};
}
